// @ts-check
import { useRef } from 'react';

import {
  FETCH_PROJECT_AREAS,
  FETCH_PROJECT_TEAMS,
  FETCH_TEAM_AREAS,
  closeWizard,
  fetchProjectAreas,
  onClickProject,
  onClickTeam,
  onCloseAreasWizard,
  onCloseTeamsWizard,
  onFinishWizardSelection,
  toggleAreaCheckboxAll
} from '../useWizardState/wizard.state.actions';
import { LBButton } from '@linearb/buzz';
import { debounce } from 'lodash';
import useWizardMenuItems from './useWizardMenuItems';

const useWizardMenus = wizardState => {
  const menuRef = useRef();
  const { teamAreas = [], selectedAreas = [] } = wizardState;

  const { areasItems } = useWizardMenuItems({ wizardState });

  const projects = {
    header: 'SELECT PROJECT',
    isOpen: wizardState.isWizardOpen,
    onClose: () => wizardState.dispatch(closeWizard()),
    isSearchable: true,
    items: wizardState.projects.map(project => ({
      id: project.id,
      title: project.name,
      onClick: () => wizardState.dispatch(onClickProject(project)),
      isActive: wizardState.selectedProject?.id === project.id
    }))
  };

  const teams = {
    header: 'SELECT TEAM',
    isOpen: !!wizardState.selectedProject,
    onClose: () => wizardState.dispatch(onCloseTeamsWizard()),
    isSearchable: true,
    isLoading: wizardState.loadingActions[FETCH_PROJECT_TEAMS],
    items: wizardState.projectTeams.map(team => ({
      id: team.id,
      title: team.name,
      onClick: () => wizardState.dispatch(onClickTeam(team)),
      isActive: wizardState.selectedTeam?.id === team.id
    })),
    actions: [
      <LBButton
        key='close'
        onClick={() => wizardState.dispatch(onCloseTeamsWizard())}
        variant='outline'
      >
        Close
      </LBButton>
    ]
  };

  const areas = {
    header: 'SELECT AREA/S',
    isOpen: wizardState.selectedTeam,
    isMultiple: true,
    isSearchable: true,
    onSearch: debounce(keyword => {
      wizardState.dispatch(fetchProjectAreas({ state: wizardState, keyword }));
    }, 400),
    isLoading:
      wizardState.loadingActions[FETCH_TEAM_AREAS] ||
      wizardState.loadingActions[FETCH_PROJECT_AREAS],
    onClose: () => wizardState.dispatch(onCloseAreasWizard()),
    selectAllEnabled: true,
    toggleSelectAll: isSelectAll =>
      wizardState.dispatch(toggleAreaCheckboxAll(isSelectAll)),
    items: areasItems,
    actions: [
      <LBButton
        key='close'
        onClick={() => wizardState.dispatch(onCloseAreasWizard())}
        variant='outline'
      >
        Close
      </LBButton>,
      <LBButton
        key='add'
        disabled={teamAreas.length + selectedAreas.length === 0}
        onClick={() =>
          wizardState.dispatch(onFinishWizardSelection(wizardState))
        }
      >
        {wizardState.isEditMode ? 'Edit' : 'Add'}
      </LBButton>
    ]
  };

  const openMenus = [projects, teams, areas].filter(menu => menu.isOpen);

  return {
    openMenus,
    menuRef
  };
};

export default useWizardMenus;
